import { getApiUrl } from '../config/api';

export interface VeoCreditBalance {
  userId: string;
  credits: number;
  totalPurchased: number;
  totalConsumed: number;
  totalSpent: number | null;
}

export interface VeoCreditTransaction {
  id: string;
  type: 'purchase' | 'consumption' | 'refund' | 'admin_adjustment';
  credits: number;
  amount?: number;
  description?: string;
  veoModel?: string;
  balanceAfter: number;
  createdAt: string;
}

export interface VeoCreditPack {
  id: string;
  credits: number;
  price: number;
  margin: string;
  stripePriceId: string;
}

export interface VeoCreditPacks {
  '5-credits': VeoCreditPack;
  '10-credits': VeoCreditPack;
  '30-credits': VeoCreditPack;
  '60-credits': VeoCreditPack;
}

class VeoCreditsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getCreditBalance(): Promise<VeoCreditBalance | null> {
    try {
      const response = await fetch(getApiUrl('/veo-credits/balance'), {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          // User hasn't purchased VEO credits yet
          return null;
        }
        throw new Error('Failed to fetch VEO credit balance');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching VEO credit balance:', error);
      return null;
    }
  }

  async getCreditHistory(limit: number = 50, offset: number = 0): Promise<VeoCreditTransaction[]> {
    try {
      const response = await fetch(
        getApiUrl(`/veo-credits/history?limit=${limit}&offset=${offset}`),
        {
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch VEO credit history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching VEO credit history:', error);
      return [];
    }
  }

  async getAvailablePacks(): Promise<VeoCreditPacks | null> {
    try {
      const response = await fetch(getApiUrl('/veo-credits/packs'), {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch VEO credit packs');
      }

      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error fetching VEO credit packs:', error);
      return null;
    }
  }

  async purchaseCredits(creditPackId: string): Promise<{ success: boolean; error?: string; checkoutUrl?: string }> {
    try {
      const response = await fetch(getApiUrl('/veo-credits/purchase'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ creditPackId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate credit purchase');
      }

      const data = await response.json();
      
      if (data.requiresImplementation) {
        throw new Error('Credit purchasing system is not yet implemented');
      }

      // If we get a checkout URL, redirect to Stripe
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return { success: true, checkoutUrl: data.checkoutUrl };
      }

      return { success: true };
    } catch (error) {
      console.error('Error purchasing VEO credits:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to purchase credits'
      };
    }
  }

  // Check if user has any VEO credits (for conditional rendering)
  async hasVeoCredits(): Promise<boolean> {
    const balance = await this.getCreditBalance();
    return balance !== null && balance.totalPurchased > 0;
  }

  // Format credit balance for display
  formatCredits(credits: number): string {
    return credits.toLocaleString();
  }

  // Format price for display
  formatPrice(price: number | null | undefined): string {
    if (price === null || price === undefined || typeof price !== 'number' || isNaN(price)) {
      return '$0.00';
    }
    return `$${price.toFixed(2)}`;
  }

  // Get best value pack recommendation
  getBestValuePack(packs: VeoCreditPacks): string {
    // 60-credits pack typically has the best value
    return '60-credits';
  }
}

export const veoCreditsService = new VeoCreditsService();
